// ═══════════════════════════════════════════════════════════════
//  Planning & Budgeting  —  Enterprise Main Entry
//  src/components/process/PlanningBudgeting/index.js
//
//  Role-based view switching:
//    CEO  → Executive overview, KPI scorecard, growth forecast
//    CFO  → Financial control, cash flow, variance waterfall
//    COO  → Operations, workforce, CapEx utilization
//    All  → Revenue Modelling, Scenario Analysis, Rolling Forecast,
//            Budget vs Actual, Approval Workflow
// ═══════════════════════════════════════════════════════════════
import React, { useState, useMemo } from "react";

// ── Dashboards ────────────────────────────────────────────────
import CEODashboard    from "./dashboards/CEODashboard";
import CFODashboard    from "./dashboards/CFODashboard";
import COODashboard    from "./dashboards/COODashboard";

// ── Modules ───────────────────────────────────────────────────
import RevenueModelling  from "./modules/RevenueModelling";
import ScenarioAnalysis  from "./modules/ScenarioAnalysis";
import RollingForecast   from "./modules/RollingForecast";
import BudgetVsActual    from "./modules/BudgetVsActual";
import ApprovalWorkflow  from "./modules/ApprovalWorkflow";

// ─────────────────────────────────────────────────────────────
//  Config
// ─────────────────────────────────────────────────────────────
const ROLES = [
  { key: "CEO", label: "CEO",  icon: "👑", color: "#7b68ee", desc: "Company overview & scorecard" },
  { key: "CFO", label: "CFO",  icon: "💼", color: "#4969ab", desc: "Financial control & cash flow" },
  { key: "COO", label: "COO",  icon: "⚙️", color: "#2d8c4e", desc: "Operations & workforce"       },
  { key: "ALL", label: "All",  icon: "📊", color: "#e67e22", desc: "Full module access"           },
];

const NAV_ITEMS = [
  // Dashboards
  { id: "ceo-dash",  label: "Executive Overview",    icon: "👑", component: <CEODashboard />,   roles: ["CEO","ALL"],        group: "Dashboards" },
  { id: "cfo-dash",  label: "Financial Control",     icon: "💼", component: <CFODashboard />,   roles: ["CFO","ALL"],        group: "Dashboards" },
  { id: "coo-dash",  label: "Operations",            icon: "⚙️", component: <COODashboard />,   roles: ["COO","ALL"],        group: "Dashboards" },
  // Modules
  { id: "revenue",   label: "Revenue Modelling",     icon: "📈", component: <RevenueModelling />, roles: ["CEO","CFO","ALL"], group: "Analysis" },
  { id: "scenario",  label: "Scenario Analysis",     icon: "🎲", component: <ScenarioAnalysis />, roles: ["CEO","CFO","ALL"], group: "Analysis" },
  { id: "forecast",  label: "Rolling Forecast",      icon: "🔮", component: <RollingForecast />,  roles: ["CEO","CFO","COO","ALL"], group: "Analysis" },
  { id: "bva",       label: "Budget vs Actual",      icon: "📉", component: <BudgetVsActual />,   roles: ["CFO","COO","ALL"], group: "Control" },
  { id: "approval",  label: "Approval Workflow",     icon: "✅", component: <ApprovalWorkflow />, roles: ["CEO","CFO","COO","ALL"], group: "Control" },
];

const GROUPS = ["Dashboards", "Analysis", "Control"];

// ─────────────────────────────────────────────────────────────
//  Sidebar NavItem
// ─────────────────────────────────────────────────────────────
const NavItem = ({ item, isActive, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "9px 16px", borderRadius: 9, cursor: "pointer",
      background: isActive ? "#4969ab" : "transparent",
      color: isActive ? "#fff" : "#555",
      fontWeight: isActive ? 700 : 400, fontSize: 13,
      transition: "all 0.15s",
      marginBottom: 2,
    }}
    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f0f4ff"; }}
    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
  >
    <span style={{ fontSize: 16, minWidth: 22, textAlign: "center" }}>{item.icon}</span>
    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {item.label}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────
//  Role Switcher
// ─────────────────────────────────────────────────────────────
const RoleSwitcher = ({ activeRole, onSelect }) => (
  <div style={{
    display: "flex", gap: 6, padding: "10px 14px",
    borderBottom: "1px solid #e8ecf4", flexWrap: "wrap",
  }}>
    {ROLES.map((r) => (
      <button key={r.key} onClick={() => onSelect(r.key)} title={r.desc} style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "5px 12px", borderRadius: 20, cursor: "pointer",
        border: `1.5px solid ${activeRole === r.key ? r.color : "#e0e6f0"}`,
        background: activeRole === r.key ? r.color : "#fff",
        color: activeRole === r.key ? "#fff" : "#666",
        fontWeight: activeRole === r.key ? 700 : 400,
        fontSize: 12, transition: "all 0.15s",
      }}>
        <span>{r.icon}</span> {r.label}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
//  Breadcrumb
// ─────────────────────────────────────────────────────────────
const Breadcrumb = ({ group, label }) => (
  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
    <span>Planning &amp; Budgeting</span>
    <span style={{ color: "#d0d8ec" }}>›</span>
    <span>{group}</span>
    <span style={{ color: "#d0d8ec" }}>›</span>
    <span style={{ color: "#4969ab", fontWeight: 600 }}>{label}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────
//  Root Component
// ─────────────────────────────────────────────────────────────
export default function PlanningBudgeting() {
  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  // Detect role from user credentials; default ALL
  const detectedRole = useMemo(() => {
    const title = (userCredentials.designation || userCredentials.role || "").toLowerCase();
    if (title.includes("ceo") || title.includes("chief executive")) return "CEO";
    if (title.includes("cfo") || title.includes("chief financial"))  return "CFO";
    if (title.includes("coo") || title.includes("chief operating"))  return "COO";
    return "ALL";
  }, [userCredentials]);

  const [activeRole,   setActiveRole]   = useState(detectedRole);
  const [activePage,   setActivePage]   = useState(null);
  const [sidebarOpen,  setSidebarOpen]  = useState(true);

  // Filtered nav based on role
  const visibleNav = useMemo(
    () => NAV_ITEMS.filter((n) => n.roles.includes(activeRole)),
    [activeRole]
  );

  // Default page when role changes
  const defaultPage = useMemo(() => visibleNav[0]?.id || null, [visibleNav]);
  const currentPage = activePage && visibleNav.find((n) => n.id === activePage)
    ? activePage
    : defaultPage;
  const activeItem  = visibleNav.find((n) => n.id === currentPage);

  const handleRoleChange = (role) => {
    setActiveRole(role);
    setActivePage(null);
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 100px)", overflow: "hidden", background: "#f4f6fb" }}>

      {/* ════ SIDEBAR ════ */}
      <div style={{
        width: sidebarOpen ? 220 : 56,
        minWidth: sidebarOpen ? 220 : 56,
        background: "#fff",
        borderRight: "1px solid #e8ecf4",
        display: "flex", flexDirection: "column",
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflow: "hidden",
        boxShadow: "2px 0 8px rgba(73,105,171,0.06)",
        zIndex: 10,
      }}>

        {/* Sidebar header */}
        <div style={{
          padding: "14px 14px 10px",
          borderBottom: "1px solid #e8ecf4",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          {sidebarOpen && (
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#1a2340", lineHeight: 1.2 }}>
                Planning &amp;<br />Budgeting
              </div>
              <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>Enterprise Suite</div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen((p) => !p)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#888", fontSize: 18, padding: 4, lineHeight: 1,
            }}
            title={sidebarOpen ? "Collapse" : "Expand"}
          >
            {sidebarOpen ? "◀" : "▶"}
          </button>
        </div>

        {/* Role switcher (only when open) */}
        {sidebarOpen && (
          <RoleSwitcher activeRole={activeRole} onSelect={handleRoleChange} />
        )}

        {/* Nav groups */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
          {GROUPS.map((group) => {
            const groupItems = visibleNav.filter((n) => n.group === group);
            if (!groupItems.length) return null;
            return (
              <div key={group} style={{ marginBottom: 8 }}>
                {sidebarOpen && (
                  <div style={{
                    fontSize: 9, fontWeight: 800, color: "#bbb",
                    textTransform: "uppercase", letterSpacing: 1.2,
                    padding: "6px 16px 4px",
                  }}>{group}</div>
                )}
                {groupItems.map((item) => (
                  sidebarOpen ? (
                    <NavItem
                      key={item.id}
                      item={item}
                      isActive={currentPage === item.id}
                      onClick={() => setActivePage(item.id)}
                    />
                  ) : (
                    <div key={item.id}
                      onClick={() => setActivePage(item.id)}
                      title={item.label}
                      style={{
                        textAlign: "center", padding: "10px 4px", cursor: "pointer",
                        borderRadius: 8, marginBottom: 2, fontSize: 18,
                        background: currentPage === item.id ? "#4969ab12" : "transparent",
                        color: currentPage === item.id ? "#4969ab" : "#888",
                        transition: "all 0.15s",
                      }}
                    >{item.icon}</div>
                  )
                ))}
              </div>
            );
          })}
        </div>

        {/* Sidebar footer */}
        {sidebarOpen && (
          <div style={{
            padding: "10px 14px",
            borderTop: "1px solid #e8ecf4",
            fontSize: 10, color: "#bbb",
          }}>
            <div>Role: <strong style={{ color: "#4969ab" }}>{activeRole}</strong></div>
            <div style={{ marginTop: 2 }}>{userCredentials.user_name || "User"}</div>
          </div>
        )}
      </div>

      {/* ════ MAIN CONTENT ════ */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

        {activeItem ? (
          <>
            {/* Breadcrumb + page title */}
            <Breadcrumb group={activeItem.group} label={activeItem.label} />
            <div style={{
              fontSize: 22, fontWeight: 900, color: "#1a2340",
              marginBottom: 20, letterSpacing: -0.5,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 26 }}>{activeItem.icon}</span>
              {activeItem.label}
            </div>

            {/* Page content */}
            {activeItem.component}
          </>
        ) : (
          /* ── Landing / home screen ── */
          <div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#1a2340", marginBottom: 4 }}>
              Welcome to Planning &amp; Budgeting
            </div>
            <div style={{ fontSize: 13, color: "#aaa", marginBottom: 30 }}>
              Enterprise-grade financial planning suite · Select a module from the sidebar
            </div>

            {/* Role cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 32 }}>
              {ROLES.filter((r) => r.key !== "ALL").map((role) => (
                <div key={role.key}
                  onClick={() => handleRoleChange(role.key)}
                  style={{
                    background: "#fff", borderRadius: 14,
                    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
                    padding: "20px 22px", cursor: "pointer",
                    borderTop: `4px solid ${role.color}`,
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 6px 24px rgba(73,105,171,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 14px rgba(73,105,171,0.09)";
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 10 }}>{role.icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: "#1a2340" }}>{role.label} View</div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{role.desc}</div>
                </div>
              ))}
            </div>

            {/* Module quick-access grid */}
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 14 }}>
              All Modules
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
              {NAV_ITEMS.map((item) => (
                <div key={item.id}
                  onClick={() => { setActiveRole("ALL"); setActivePage(item.id); }}
                  style={{
                    background: "#fff", borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(73,105,171,0.08)",
                    padding: "14px 16px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f4ff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                >
                  <span style={{ fontSize: 22 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#1a2340" }}>{item.label}</div>
                    <div style={{ fontSize: 10, color: "#aaa" }}>{item.group}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
